import React from "react";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Droplets, FlaskConical, Moon, Brain, Salad,
  Users, Baby, Stethoscope, PhoneCall, MessageCircle, Heart, Apple,
  ShieldCheck, Sparkles
} from "lucide-react";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
const stagger = (i) => ({ ...fadeUp, transition: { duration: 0.6, delay: i * 0.15 } });

const PROGRAMS = [
  {
    id: "elite",
    name: "Fitly Elite",
    tagline: "Precision Fertility Biohacking",
    description: "A fully online fertility nutrition program built around five interconnected biological systems — with a structured 12-week curriculum that builds real nutrition literacy.",
    period: "1 / 3 / 6 months — Online, Worldwide",
    priceFrom: "12,000",
    href: "/programs/elite",
    pillars: [
      { icon: Droplets, label: "Cycle-Synced" },
      { icon: FlaskConical, label: "Biomarker" },
      { icon: Salad, label: "Gut-Hormone" },
      { icon: Moon, label: "Circadian" },
      { icon: Brain, label: "Stress" },
    ],
    highlights: [
      "Her Track, His Track, or Couples Track",
      "1:1 WhatsApp dietitian support",
      "Live weekly group coaching calls",
      "Specialist input as your case requires",
    ],
  },
  {
    id: "premium-plus",
    name: "Fitly Premium Plus",
    tagline: "Conception to Motherhood",
    description: "The most comprehensive program — up to 9 months of end-to-end support from preconception through pregnancy, delivery, and postnatal recovery.",
    period: "Up to 9 months of dedicated support",
    price: "85,000",
    badge: "Most Comprehensive",
    href: "/programs/premium-plus",
    highlights: [
      "Everything in Fitly Elite included",
      "Nadiya's personal guidance throughout",
      "Unlimited specialist consultations",
      "Live Yoga 5 days/week + care coordinator",
    ],
    extras: [
      { icon: Baby, label: "Prenatal" },
      { icon: Apple, label: "Postnatal" },
      { icon: Stethoscope, label: "Specialists" },
      { icon: ShieldCheck, label: "Coordinator" },
    ],
  },
];

export default function ProgramsSection() {
  return (
    <section id="programs" data-testid="programs-section" className="py-28 md:py-36 bg-[#0D1F17] relative overflow-hidden">
      {/* Food background image — seamlessly blending */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1920&q=80"
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#0D1F17]/[0.92]" />
      </div>
      {/* Background effects */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")" }} />
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#D4AF37]/3 blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#A8D5BA]/3 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div {...fadeUp} className="text-center mb-20">
          <span className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#D4AF37]">Investment in Your Health</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-[#F7F5F0] mt-4">
            Our <span className="italic font-light text-[#D4AF37]">Programs</span>
          </h2>
          <p className="text-[#A3B8AD] mt-4 max-w-xl mx-auto text-sm leading-relaxed">
            Clinically guided fertility nutrition — choose the depth of support that fits your journey.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {PROGRAMS.map((program, i) => (
            <motion.div key={program.id} {...stagger(i)}
              data-testid={`program-card-${program.id}`}
              className="relative group bg-gradient-to-b from-[#132A20]/80 to-[#0A1711]/60 border border-[#F7F5F0]/6 rounded-3xl p-8 md:p-10 hover:border-[#D4AF37]/25 transition-all duration-500 flex flex-col backdrop-blur-sm">

              {program.badge && (
                <span className="absolute -top-3 right-8 bg-[#D4AF37] text-[#0D1F17] text-[10px] font-bold tracking-widest uppercase px-5 py-1.5 rounded-full shadow-lg shadow-[#D4AF37]/20">
                  {program.badge}
                </span>
              )}

              {/* Header */}
              <div className="mb-8">
                <h3 className="font-serif text-3xl md:text-4xl text-[#F7F5F0] tracking-tight">{program.name}</h3>
                <p className="text-[#D4AF37] text-sm mt-2 font-medium tracking-wide">{program.tagline}</p>
                <p className="text-[#A3B8AD]/60 text-xs mt-1 tracking-wider">{program.period}</p>
              </div>

              <p className="text-sm text-[#F7F5F0]/60 leading-relaxed mb-8">{program.description}</p>

              {/* Pillar icons for Elite */}
              {program.pillars && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {program.pillars.map((p) => (
                    <div key={p.label} className="flex items-center gap-2 bg-[#F7F5F0]/5 border border-[#F7F5F0]/8 px-3 py-2 rounded-xl">
                      <p.icon size={14} className="text-[#D4AF37]" />
                      <span className="text-[11px] text-[#A3B8AD] tracking-wide">{p.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Extra icons for Premium Plus */}
              {program.extras && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {program.extras.map((p) => (
                    <div key={p.label} className="flex items-center gap-2 bg-[#D4AF37]/5 border border-[#D4AF37]/15 px-3 py-2 rounded-xl">
                      <p.icon size={14} className="text-[#D4AF37]" />
                      <span className="text-[11px] text-[#D4AF37]/80 tracking-wide">{p.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Highlights */}
              <ul className="space-y-3 mb-8 flex-grow">
                {program.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={10} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-sm text-[#F7F5F0]/70 leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>

              {/* Price + CTA */}
              <div className="mt-auto pt-8 border-t border-[#F7F5F0]/5">
                <div className="mb-5">
                  {program.priceFrom ? (
                    <p className="font-serif text-[#F7F5F0]">
                      <span className="text-[#A3B8AD]/50 text-xs mr-1.5">Starting from</span>
                      <span className="text-4xl">₹{program.priceFrom}</span>
                      <span className="text-[#A3B8AD]/50 text-sm ml-1.5">/ month</span>
                    </p>
                  ) : (
                    <p className="font-serif text-4xl text-[#F7F5F0]">₹{program.price}</p>
                  )}
                </div>

                <a href={program.href} data-testid={`btn-view-${program.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0D1F17] py-4 text-sm font-bold tracking-wide rounded-full hover:bg-[#B5952F] transition-all duration-300 shadow-lg shadow-[#D4AF37]/15 group-hover:shadow-[#D4AF37]/25">
                  View Full Program <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <RefundPolicy />
      </div>
    </section>
  );
}

function RefundPolicy() {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <div className="mt-20 max-w-3xl mx-auto" data-testid="refund-policy">
      <button onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-[#A3B8AD]/40 hover:text-[#A3B8AD] transition-colors text-sm mx-auto"
        data-testid="refund-policy-toggle">
        <ShieldCheck size={14} />
        <span>Payment & Refund Policy</span>
      </button>
      {isOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.3 }}
          className="overflow-hidden">
          <div className="mt-6 p-8 rounded-xl bg-[#0A1711]/50 border border-[#F7F5F0]/5 text-[#A3B8AD]/60 text-sm leading-relaxed space-y-3">
            <p>Applicable to Fitly Elite and Fitly Premium Plus programs.</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>The complete program fee must be paid in advance before the program begins.</li>
              <li>Once enrolled and the program has commenced, the fee is non-refundable and non-transferable.</li>
              <li>Diet plans, assessments and program structure are prepared in advance based on the client&apos;s health information.</li>
              <li>Please review program inclusions, duration and pricing carefully before completing payment.</li>
              <li>This policy is subject to applicable consumer-protection laws.</li>
            </ul>
          </div>
        </motion.div>
      )}
    </div>
  );
}
